import React, { useEffect, useState, useCallback } from "react";
import { LayoutDashboard, Users, Store, Package, ShoppingCart, CreditCard, Wallet,
  Landmark, Flag, Settings, ScrollText, Search, Tag, ShieldCheck, Gavel, FileText } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { rp, fmtDate } from "@/lib/format";
import { Layout } from "@/components/Layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState({});
  const [lists, setLists] = useState({});
  const [trace, setTrace] = useState(null);
  const [nmid, setNmid] = useState("");
  const [config, setConfig] = useState(null);
  const [taraBalance, setTaraBalance] = useState(null);
  const [docModal, setDocModal] = useState(null);
  const [reasonModal, setReasonModal] = useState(null);
  const [reasonText, setReasonText] = useState("");

  const fetchList = useCallback((key, url) =>
    api.get(url).then(({ data }) => setLists((l) => ({ ...l, [key]: data }))).catch(() => {}), []);

  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  useEffect(() => {
    const map = {
      sellers: "/admin/sellers", stores: "/admin/stores", moderation: "/admin/products?moderation=pending",
      orders: "/admin/orders", payments: "/admin/payments", entitlements: "/admin/entitlements",
      settlements: "/admin/settlements", payprofiles: "/admin/payment-profiles", banks: "/admin/bank-accounts",
      reports: "/admin/reports", reviews: "/admin/reviews", users: "/admin/users",
      disputes: "/admin/disputes",
      audit: "/admin/audit-logs", categories: "/categories",
    };
    if (map[tab]) fetchList(tab, map[tab]);
    if (tab === "config" && !config) api.get("/admin/config").then(({ data }) => setConfig(data)).catch(() => {});
    if (tab === "tarapay") {
      api.get("/admin/tarapay/balance").then(({ data }) => setTaraBalance(data)).catch(() => {});
      fetchList("callbacks", "/admin/tarapay/callbacks");
    }
  }, [tab, fetchList, config]);

  const refreshStats = () => api.get("/admin/stats").then(({ data }) => setStats(data));

  const act = async (fn, key, url) => { try { await fn(); toast.success("Berhasil"); fetchList(key, url); refreshStats(); } catch (e) { toast.error(errMsg(e)); } };
  const moderate = (id, decision) => act(() => api.put(`/admin/products/${id}/moderate`, { decision }), "moderation", "/admin/products?moderation=pending");
  const verifySeller = (id) => act(() => api.put(`/admin/sellers/${id}/verify`), "sellers", "/admin/sellers");
  const rejectSeller = (id, note) => act(() => api.put(`/admin/sellers/${id}/reject`, { note }), "sellers", "/admin/sellers");
  const freezeStore = (id, path, reason) => act(() => api.put(`/admin/stores/${id}/${path}`, reason !== undefined ? { reason } : undefined), "stores", "/admin/stores");
  const openReason = (title, hint, onConfirm) => { setReasonText(""); setReasonModal({ title, hint, onConfirm }); };
  const verifyBank = (id) => act(() => api.put(`/admin/bank-accounts/${id}/verify`), "banks", "/admin/bank-accounts");
  const processSettlement = (id) => act(() => api.put(`/admin/settlements/${id}/process`), "settlements", "/admin/settlements");
  const resolveReport = (id) => act(() => api.put(`/admin/reports/${id}/resolve`, { status: "resolved" }), "reports", "/admin/reports");
  const resolveDispute = (id, resolution, note) => act(() => api.put(`/admin/disputes/${id}/resolve`, { resolution, note }), "disputes", "/admin/disputes");
  const approveNmid = (id) => act(() => api.put(`/admin/payment-profiles/${id}/approve`, { nmid }), "payprofiles", "/admin/payment-profiles");
  const loadTrace = async (id) => {
    try {
      const { data } = await api.get(`/admin/traceability/${id}`);
      setTrace(data);
      fetchList("settlements", "/admin/settlements");
      refreshStats();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const downloadSettlementInvoice = async (s) => {
    const tid = toast.loading("Menyiapkan invoice settlement PDF...");
    try {
      const res = await api.get(`/seller/settlements/${s.id}/invoice`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Settlement-Invoice-${s.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.dismiss(tid);
      toast.success("Invoice settlement berhasil diunduh");
    } catch (e) {
      toast.dismiss(tid);
      toast.error(errMsg(e, "Gagal mengunduh invoice settlement"));
    }
  };

  const saveConfig = async () => {
    try {
      await api.put("/admin/config/brand", config.brand);
      await api.put("/admin/config/platform", { platform_fee_percent: Number(config.platform.platform_fee_percent), payment_partner: config.platform.payment_partner });
      toast.success("Konfigurasi disimpan");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const NAV = [
    ["dashboard", "Dashboard", LayoutDashboard], ["sellers", "Sellers", Users], ["stores", "Stores", Store],
    ["moderation", "Moderasi", Package], ["orders", "Orders", ShoppingCart], ["payments", "Payments", CreditCard],
    ["entitlements", "Entitlements", Wallet], ["settlements", "Settlements", Landmark], ["payprofiles", "Payment Profiles", CreditCard],
    ["banks", "Bank Accounts", Landmark], ["reports", "Risk/Reports", Flag], ["disputes", "Sengketa", Gavel], ["reviews", "Reviews", ShieldCheck],
    ["categories", "Categories", Tag], ["users", "Users", Users], ["audit", "Audit Logs", ScrollText],
    ["tarapay", "TaraPay", CreditCard], ["config", "Configuration", Settings],
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
        <h1 className="font-serif text-3xl font-bold text-rl-ink">Admin · PT Raksa Madha Karya</h1>
        <p className="text-sm text-rl-muted mt-1">Kelola seluruh marketplace preloved.</p>

        <div className="mt-6 grid lg:grid-cols-[220px_1fr] gap-6">
          <aside className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2">
            {NAV.map(([k, label, Icon]) => (
              <button key={k} data-testid={`admin-nav-${k}`} onClick={() => setTab(k)}
                className={`flex items-center gap-2 shrink-0 rounded-lg px-3 py-2 text-sm font-semibold text-left transition-colors ${tab === k ? "bg-rl-primary text-white" : "text-rl-ink2 hover:bg-rl-bg2"}`}>
                <Icon className="w-4 h-4" />{label}</button>
            ))}
          </aside>

          <div className="min-w-0">
            {tab === "dashboard" && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[["Sellers", stats.sellers], ["Stores", stats.stores], ["Produk", stats.products],
                  ["Moderasi Pending", stats.pending_moderation], ["Orders", stats.orders], ["Selesai", stats.completed_orders],
                  ["Laporan Terbuka", stats.open_reports]].map(([l, v]) => (
                  <div key={l} className="rounded-2xl border border-rl-border bg-white p-4"><div className="text-2xl font-bold text-rl-ink">{v ?? 0}</div><div className="text-xs text-rl-muted mt-1">{l}</div></div>))}
                <div className="rounded-2xl border border-rl-border bg-rl-secondary text-white p-4"><div className="text-xl font-bold">{rp(stats.gmv)}</div><div className="text-xs text-white/70 mt-1">GMV (dibayar)</div></div>
                <div className="rounded-2xl border border-rl-border bg-rl-primary text-white p-4"><div className="text-xl font-bold">{rp(stats.platform_revenue)}</div><div className="text-xs text-white/80 mt-1">Pendapatan Platform (fee)</div></div>
                <div className="rounded-2xl border border-rl-border bg-white p-4"><div className="text-xl font-bold text-rl-ink">{rp(stats.settled_amount)}</div><div className="text-xs text-rl-muted mt-1">Total Settled</div></div>
              </div>
            )}

            {tab === "sellers" && (
              <Table head={["Tipe", "KYC", "Store", "Lokasi", "Dokumen", "Aksi"]}>
                {(lists.sellers || []).map((s) => (
                  <tr key={s.id} data-testid={`admin-seller-${s.id}`} className="border-t border-rl-border">
                    <td className="p-3 capitalize">{s.seller_type}</td>
                    <td className="p-3"><Badge ok={s.kyc_status === "verified"}>{s.kyc_status}</Badge></td>
                    <td className="p-3">{s.store?.name}</td><td className="p-3">{s.store?.location}</td>
                    <td className="p-3">{s.documents && Object.keys(s.documents).length
                      ? <button data-testid={`docs-${s.id}`} onClick={() => setDocModal({ name: s.store?.name || s.seller_type, documents: s.documents })} className="text-rl-secondary font-semibold text-sm">Lihat ({Object.keys(s.documents).length})</button>
                      : <span className="text-rl-muted text-xs">—</span>}</td>
                    <td className="p-3 flex flex-wrap gap-3">
                      {s.kyc_status !== "verified" && <button data-testid={`verify-seller-${s.id}`} onClick={() => verifySeller(s.id)} className="text-emerald-700 font-semibold text-sm">Verifikasi</button>}
                      {s.kyc_status !== "rejected" && <button data-testid={`reject-seller-${s.id}`} onClick={() => openReason("Tolak KYC Seller", "Alasan penolakan (mis. dokumen buram / tidak sesuai)", (note) => rejectSeller(s.id, note))} className="text-rose-600 font-semibold text-sm">Tolak</button>}
                    </td>
                  </tr>))}
              </Table>
            )}

            {tab === "stores" && (
              <Table head={["Nama", "Slug", "Tipe", "Verified", "Status Toko", "Status Saldo", "Aksi"]}>
                {(lists.stores || []).map((s) => (
                  <tr key={s.id} data-testid={`admin-store-${s.id}`} className="border-t border-rl-border">
                    <td className="p-3 font-semibold">{s.name}</td><td className="p-3 font-mono text-xs">{s.slug}</td>
                    <td className="p-3 capitalize">{s.seller_type}</td><td className="p-3">{s.verified ? "✓" : "—"}</td>
                    <td className="p-3">{s.frozen
                      ? <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-rose-100 text-rose-800" title={s.frozen_reason}>Dibekukan</span>
                      : <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-800">Aktif</span>}</td>
                    <td className="p-3">{s.balance_frozen
                      ? <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-rose-100 text-rose-800" title={s.balance_frozen_reason}>Saldo Beku</span>
                      : <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-800">Normal</span>}</td>
                    <td className="p-3 flex flex-wrap gap-3">
                      {s.frozen
                        ? <button data-testid={`unfreeze-store-${s.id}`} onClick={() => freezeStore(s.id, "unfreeze")} className="text-emerald-700 font-semibold text-sm">Aktifkan Toko</button>
                        : <button data-testid={`freeze-store-${s.id}`} onClick={() => openReason("Bekukan Toko", "Toko disembunyikan dari publik & login seller diblokir. Alasan:", (reason) => freezeStore(s.id, "freeze", reason))} className="text-rose-600 font-semibold text-sm">Bekukan Toko</button>}
                      {s.balance_frozen
                        ? <button data-testid={`unfreeze-balance-${s.id}`} onClick={() => freezeStore(s.id, "unfreeze-balance")} className="text-emerald-700 font-semibold text-sm">Cairkan Saldo</button>
                        : <button data-testid={`freeze-balance-${s.id}`} onClick={() => openReason("Bekukan Saldo", "Seller tidak bisa mengajukan pencairan baru. Alasan:", (reason) => freezeStore(s.id, "freeze-balance", reason))} className="text-amber-600 font-semibold text-sm">Bekukan Saldo</button>}
                    </td></tr>))}
              </Table>
            )}

            {tab === "moderation" && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {(lists.moderation || []).length === 0 && <p className="text-rl-muted">Tidak ada produk menunggu moderasi.</p>}
                {(lists.moderation || []).map((p) => (
                  <div key={p.id} data-testid={`mod-product-${p.id}`} className="rounded-xl border border-rl-border bg-white p-3">
                    <div className="w-full h-32 rounded-lg overflow-hidden bg-rl-bg2">{(p.photos || [])[0] && <img src={p.photos[0]} alt="" className="w-full h-full object-cover" />}</div>
                    <div className="mt-2 font-semibold text-sm">{p.title}</div><div className="text-sm font-bold">{rp(p.price)}</div>
                    {p.defect_notes && <div className="text-xs text-amber-700 mt-1">Minus: {p.defect_notes}</div>}
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <button data-testid={`approve-${p.id}`} onClick={() => moderate(p.id, "approved")} className="rounded-lg bg-emerald-600 text-white text-xs font-semibold py-2">Setujui</button>
                      <button data-testid={`reject-${p.id}`} onClick={() => moderate(p.id, "rejected")} className="rounded-lg bg-rose-600 text-white text-xs font-semibold py-2">Tolak</button>
                    </div>
                  </div>))}
              </div>
            )}

            {tab === "orders" && (
              <Table head={["Order", "Produk", "Buyer", "Jumlah", "Fee", "Status"]}>
                {(lists.orders || []).map((o) => (
                  <tr key={o.id} className="border-t border-rl-border">
                    <td className="p-3 font-mono text-xs">{o.order_number}</td><td className="p-3">{o.product_snapshot?.title}</td>
                    <td className="p-3">{o.buyer_name}</td><td className="p-3">{rp(o.amount)}</td><td className="p-3 text-rl-muted">{rp(o.platform_fee)}</td>
                    <td className="p-3 capitalize">{o.status}</td></tr>))}
              </Table>
            )}

            {tab === "payments" && (
              <Table head={["Ref", "Order", "Provider", "NMID", "Metode", "Jumlah", "Status"]}>
                {(lists.payments || []).map((p) => (
                  <tr key={p.id} className="border-t border-rl-border">
                    <td className="p-3 font-mono text-xs">{p.provider_ref}</td><td className="p-3 font-mono text-xs">{p.order_id.slice(0, 8)}</td>
                    <td className="p-3 capitalize">{p.provider}{p.mocked && <span className="ml-1 text-[10px] text-amber-600 font-semibold">MOCK</span>}</td>
                    <td className="p-3 font-mono text-xs">{p.merchant_nmid || "—"}</td><td className="p-3 uppercase">{p.method}</td>
                    <td className="p-3">{rp(p.amount)}</td><td className="p-3">{p.status}</td></tr>))}
              </Table>
            )}

            {tab === "entitlements" && (
              <Table head={["Order", "Produk", "Bruto", "Fee", "Net", "Status"]}>
                {(lists.entitlements || []).map((e) => (
                  <tr key={e.id} className="border-t border-rl-border">
                    <td className="p-3 font-mono text-xs">{e.order_number}</td><td className="p-3">{e.product_title}</td>
                    <td className="p-3">{rp(e.gross_amount)}</td><td className="p-3 text-rl-muted">{rp(e.platform_fee)}</td>
                    <td className="p-3 font-bold text-rl-secondary">{rp(e.net_amount)}</td><td className="p-3 capitalize">{e.status}</td></tr>))}
              </Table>
            )}

            {tab === "settlements" && (
              <Table head={["ID", "Store", "Rekening", "Jumlah", "Status", "Aksi"]}>
                {(lists.settlements || []).map((s) => (
                  <tr key={s.id} data-testid={`admin-settlement-${s.id}`} className="border-t border-rl-border">
                    <td className="p-3 font-mono text-xs">{s.id.slice(0, 8)}</td><td className="p-3">{s.store_name}</td>
                    <td className="p-3 text-xs">{s.bank_snapshot?.bank_name} {s.bank_snapshot?.account_number}<br />a.n {s.bank_snapshot?.account_holder}</td>
                    <td className="p-3 font-bold text-rl-secondary">{rp(s.amount)}</td>
                    <td className="p-3"><Badge ok={s.status === "settled"}>{s.status}</Badge></td>
                    <td className="p-3 flex gap-2 items-center">
                      <button data-testid={`admin-invoice-settlement-${s.id}`} onClick={() => downloadSettlementInvoice(s)} className="text-rl-primary font-semibold text-sm flex items-center gap-1" title="Unduh Invoice PDF"><FileText className="w-3.5 h-3.5" />Invoice</button>
                      {s.status !== "settled" && <button data-testid={`process-settlement-${s.id}`} onClick={() => processSettlement(s.id)} className="text-rl-primary font-semibold text-sm">Proses Payout</button>}
                      <button data-testid={`trace-settlement-${s.id}`} onClick={() => loadTrace(s.id)} className="text-rl-secondary font-semibold text-sm flex items-center gap-1"><Search className="w-3.5 h-3.5" />Telusuri</button>
                    </td></tr>))}
              </Table>
            )}

            {tab === "payprofiles" && (
              <Table head={["Store", "Partner", "NMID", "Status", "Aksi"]}>
                {(lists.payprofiles || []).map((p) => (
                  <tr key={p.id} className="border-t border-rl-border">
                    <td className="p-3">{p.store_name}</td><td className="p-3 capitalize">{p.payment_partner}</td>
                    <td className="p-3 font-mono text-xs">{p.nmid || "—"}</td><td className="p-3"><Badge ok={p.status === "approved"}>{p.status}</Badge></td>
                    <td className="p-3">{p.status !== "approved" && (
                      <Dialog><DialogTrigger asChild><button data-testid={`approve-nmid-${p.id}`} className="text-rl-primary font-semibold text-sm">Set NMID</button></DialogTrigger>
                        <DialogContent><DialogHeader><DialogTitle>Approve Merchant Identity</DialogTitle></DialogHeader>
                          <p className="text-xs text-rl-muted">Masukkan NMID dari partner pembayaran (bukan generate acak).</p>
                          <input data-testid="nmid-input" value={nmid} onChange={(e) => setNmid(e.target.value)} placeholder="NMID" className="w-full rounded-lg border border-rl-border px-3 py-2 text-sm" />
                          <DialogFooter><button data-testid={`submit-nmid-${p.id}`} onClick={() => approveNmid(p.id)} disabled={!nmid} className="rounded-full bg-rl-primary text-white font-bold px-6 py-2.5 disabled:opacity-50">Simpan</button></DialogFooter>
                        </DialogContent></Dialog>)}</td></tr>))}
              </Table>
            )}

            {tab === "banks" && (
              <Table head={["Bank", "No. Rekening", "Atas Nama", "Verified", "Aksi"]}>
                {(lists.banks || []).map((b) => (
                  <tr key={b.id} className="border-t border-rl-border">
                    <td className="p-3">{b.bank_name}</td><td className="p-3 font-mono">{b.account_number}</td><td className="p-3">{b.account_holder}</td>
                    <td className="p-3"><Badge ok={b.verified}>{b.verified ? "Ya" : "Belum"}</Badge></td>
                    <td className="p-3">{!b.verified && <button data-testid={`verify-bank-${b.id}`} onClick={() => verifyBank(b.id)} className="text-rl-primary font-semibold text-sm">Verifikasi</button>}</td></tr>))}
              </Table>
            )}

            {tab === "reports" && (
              <Table head={["Tipe", "Alasan", "Produk", "Status", "Aksi"]}>
                {(lists.reports || []).map((r) => (
                  <tr key={r.id} className="border-t border-rl-border">
                    <td className="p-3 capitalize">{r.type}</td><td className="p-3">{r.reason}</td><td className="p-3 font-mono text-xs">{r.product_id?.slice(0, 8)}</td>
                    <td className="p-3"><Badge ok={r.status === "resolved"}>{r.status}</Badge></td>
                    <td className="p-3">{r.status === "open" && <button data-testid={`resolve-report-${r.id}`} onClick={() => resolveReport(r.id)} className="text-rl-primary font-semibold text-sm">Tandai Selesai</button>}</td></tr>))}
              </Table>
            )}

            {tab === "disputes" && (
              <div className="space-y-4">
                {(lists.disputes || []).length === 0 && <p className="text-rl-muted">Tidak ada sengketa.</p>}
                {(lists.disputes || []).map((d) => (
                  <div key={d.id} data-testid={`dispute-${d.id}`} className="rounded-xl border border-rl-border bg-white p-4">
                    <div className="flex justify-between flex-wrap gap-2">
                      <div>
                        <div className="font-mono text-xs text-rl-muted">{d.order_number} · {d.store_name}</div>
                        <div className="font-semibold text-rl-ink">{d.product_title} · {rp(d.amount)}</div>
                        <div className="text-xs mt-0.5">Metode: <span className="uppercase font-semibold">{d.payment_mode}</span> · dibuka oleh <b className="capitalize">{d.opened_by}</b></div>
                      </div>
                      <Badge ok={d.status === "resolved"}>{d.status === "resolved" ? `Selesai · ${d.resolution}` : "Terbuka"}</Badge>
                    </div>
                    <p className="mt-2 text-sm text-rl-ink2"><b>Alasan:</b> {d.reason}</p>
                    {d.note && <p className="text-sm text-rl-muted"><b>Catatan admin:</b> {d.note}</p>}
                    {d.status === "open" && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button data-testid={`resolve-buyer-${d.id}`} onClick={() => openReason("Menangkan Buyer", "Dana ditahan tidak dirilis ke seller & order di-refund. Catatan keputusan:", (n) => resolveDispute(d.id, "buyer_favor", n))} className="rounded-lg bg-rose-600 text-white text-xs font-semibold px-3 py-2">Menangkan Buyer</button>
                        <button data-testid={`resolve-seller-${d.id}`} onClick={() => openReason("Menangkan Seller", "Dana dirilis ke seller. Catatan keputusan:", (n) => resolveDispute(d.id, "seller_favor", n))} className="rounded-lg bg-emerald-600 text-white text-xs font-semibold px-3 py-2">Menangkan Seller</button>
                        <button data-testid={`cancel-order-${d.id}`} onClick={() => openReason("Batalkan Order", "Order dibatalkan & produk dikembalikan ke etalase. Catatan:", (n) => resolveDispute(d.id, "order_cancelled", n))} className="rounded-lg bg-amber-600 text-white text-xs font-semibold px-3 py-2">Batalkan Order</button>
                        <button data-testid={`close-dispute-${d.id}`} onClick={() => openReason("Tutup Sengketa", "Sengketa ditutup tanpa perubahan, alur normal dilanjutkan. Catatan:", (n) => resolveDispute(d.id, "closed", n))} className="rounded-lg border border-rl-border text-rl-ink text-xs font-semibold px-3 py-2">Tutup</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {tab === "reviews" && (
              <Table head={["Buyer", "Rating", "Komentar", "Produk"]}>
                {(lists.reviews || []).map((r) => (
                  <tr key={r.id} className="border-t border-rl-border"><td className="p-3">{r.buyer_name}</td><td className="p-3">{r.rating}★</td><td className="p-3">{r.comment}</td><td className="p-3">{r.product_title}</td></tr>))}
              </Table>
            )}

            {tab === "categories" && (
              <Table head={["Nama (ID)", "Nama (EN)", "Slug", "Icon"]}>
                {(lists.categories || []).map((c) => (
                  <tr key={c.id} className="border-t border-rl-border"><td className="p-3">{c.name}</td><td className="p-3">{c.name_en}</td><td className="p-3 font-mono text-xs">{c.slug}</td><td className="p-3">{c.icon}</td></tr>))}
              </Table>
            )}

            {tab === "users" && (
              <Table head={["Nama", "Email", "Role", "Bergabung"]}>
                {(lists.users || []).map((u) => (
                  <tr key={u.id} className="border-t border-rl-border"><td className="p-3">{u.name}</td><td className="p-3">{u.email}</td><td className="p-3 capitalize">{u.role}</td><td className="p-3">{fmtDate(u.created_at)}</td></tr>))}
              </Table>
            )}

            {tab === "audit" && (
              <Table head={["Waktu", "Aktor", "Aksi", "Entity", "ID"]}>
                {(lists.audit || []).map((a) => (
                  <tr key={a.id} className="border-t border-rl-border"><td className="p-3 text-xs">{fmtDate(a.timestamp)}</td><td className="p-3 text-xs">{a.actor_email}</td><td className="p-3 font-mono text-xs">{a.action}</td><td className="p-3">{a.entity}</td><td className="p-3 font-mono text-xs">{a.entity_id?.slice(0, 8)}</td></tr>))}
              </Table>
            )}

            {tab === "tarapay" && (
              <div className="space-y-6">
                <div className="rounded-2xl border border-rl-border bg-white p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-rl-ink">Saldo Merchant TaraPay</h3>
                    <button data-testid="refresh-balance" onClick={() => api.get("/admin/tarapay/balance").then(({ data }) => setTaraBalance(data))} className="text-sm font-semibold text-rl-primary">Refresh</button>
                  </div>
                  {!taraBalance ? <p className="text-rl-muted text-sm mt-3">Memuat...</p> :
                    (taraBalance.error || taraBalance?.data?.code) ? (
                      <p className="mt-3 text-sm text-rose-600">{taraBalance.error || taraBalance?.data?.message || "Tidak dapat mengambil saldo"}</p>
                    ) : (
                      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[["Tersedia", taraBalance.data?.data?.availableBalance], ["Tertahan", taraBalance.data?.data?.frozenBalance], ["Pending Settle", taraBalance.data?.data?.pendingSettlementAmount], ["Total", taraBalance.data?.data?.totalBalance]].map(([l, v]) => (
                          <div key={l} className="rounded-xl bg-rl-bg2 p-3"><div className="text-xs text-rl-muted">{l}</div><div className="font-bold text-rl-ink">{v ? rp(Math.round(Number(v))) : "-"}</div></div>))}
                      </div>
                    )}
                  <p className="mt-3 text-xs text-rl-muted">Base: openapi.tarapay-id.com · Jika muncul "IP not in whitelist", daftarkan IP server di dashboard TaraPay.</p>
                </div>
                <div>
                  <h3 className="font-bold text-rl-ink mb-3">Log Callback TaraPay</h3>
                  <Table head={["Waktu", "Tipe", "Order", "Status", "Signature"]}>
                    {(lists.callbacks || []).map((c) => (
                      <tr key={c.id} className="border-t border-rl-border">
                        <td className="p-3 text-xs">{fmtDate(c.created_at)}</td><td className="p-3 capitalize">{c.type}</td>
                        <td className="p-3 font-mono text-xs">{c.mch_order_no?.slice(0, 10)}</td><td className="p-3">{c.order_status}</td>
                        <td className="p-3"><Badge ok={c.signature_valid}>{c.signature_valid ? "valid" : "invalid"}</Badge></td></tr>))}
                  </Table>
                </div>
              </div>
            )}

            {tab === "config" && config && (
              <div className="space-y-6 max-w-lg">
                <div className="rounded-2xl border border-rl-border bg-white p-5">
                  <h3 className="font-bold text-rl-ink mb-3">Brand Settings</h3>
                  {[["brand_name", "Brand Name"], ["tagline", "Tagline"], ["tagline_en", "Tagline (EN)"], ["sub_tagline", "Sub Tagline"], ["legal_company_name", "Legal Company"], ["support_email", "Support Email"], ["support_phone", "Support Phone"]].map(([k, label]) => (
                    <div key={k} className="mb-3"><label className="text-xs font-semibold text-rl-muted">{label}</label>
                      <input data-testid={`cfg-${k}`} value={config.brand?.[k] || ""} onChange={(e) => setConfig({ ...config, brand: { ...config.brand, [k]: e.target.value } })} className="w-full rounded-lg border border-rl-border px-3 py-2 text-sm mt-1" /></div>))}
                  <div className="grid grid-cols-3 gap-3">
                    {["primary_color", "secondary_color", "accent_color"].map((k) => (
                      <div key={k}><label className="text-xs font-semibold text-rl-muted">{k.replace("_color", "")}</label>
                        <input type="color" data-testid={`cfg-${k}`} value={config.brand?.[k] || "#000000"} onChange={(e) => setConfig({ ...config, brand: { ...config.brand, [k]: e.target.value } })} className="w-full h-9 rounded-lg border border-rl-border mt-1" /></div>))}
                  </div>
                </div>
                <div className="rounded-2xl border border-rl-border bg-white p-5">
                  <h3 className="font-bold text-rl-ink mb-3">Platform & Fees</h3>
                  <label className="text-xs font-semibold text-rl-muted">Platform Fee (%)</label>
                  <input data-testid="cfg-fee" type="number" step="0.1" value={config.platform?.platform_fee_percent ?? ""} onChange={(e) => setConfig({ ...config, platform: { ...config.platform, platform_fee_percent: e.target.value } })} className="w-full rounded-lg border border-rl-border px-3 py-2 text-sm mt-1" />
                  <label className="text-xs font-semibold text-rl-muted mt-3 block">Payment Partner</label>
                  <select data-testid="cfg-partner" value={config.platform?.payment_partner || "easylink"} onChange={(e) => setConfig({ ...config, platform: { ...config.platform, payment_partner: e.target.value } })} className="w-full rounded-lg border border-rl-border px-3 py-2 text-sm mt-1">
                    <option value="easylink">EasyLink</option><option value="ayolinx">AyoLinx</option><option value="tarapay">TaraPay</option></select>
                </div>
                <button data-testid="save-config" onClick={saveConfig} className="rounded-full bg-rl-primary text-white font-bold px-8 py-3">Simpan Konfigurasi</button>
              </div>
            )}
          </div>
        </div>

        {/* Traceability modal */}
        <Dialog open={!!trace} onOpenChange={(o) => !o && setTrace(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Penelusuran Settlement</DialogTitle></DialogHeader>
            {trace && (
              <div className="space-y-3 text-sm">
                <div className="rounded-xl bg-rl-secondary/5 border border-rl-secondary/20 p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-bold text-rl-secondary">Kenapa {rp(trace.settlement.amount)} ditransfer?</div>
                    <Badge ok={trace.settlement.status === "settled"}>{trace.settlement.status}</Badge>
                  </div>
                  <div className="mt-1 text-rl-ink2">Ke: {trace.settlement.bank_snapshot?.account_holder} · {trace.settlement.bank_snapshot?.bank_name} {trace.settlement.bank_snapshot?.account_number}</div>
                  <div className="text-xs text-rl-muted">Ref: {trace.settlement.reference}</div>
                </div>
                <Chain label="Seller" val={`${trace.store?.name} (${trace.seller?.seller_type})`} />
                {trace.chain.map((c, i) => (
                  <div key={i} className="rounded-xl border border-rl-border p-4 space-y-1">
                    <Chain label="Order" val={`${c.order?.order_number} · ${c.order?.status}`} />
                    <Chain label="Produk" val={c.product?.title} />
                    <Chain label="Pembayaran" val={`${rp(c.payment?.amount)} (${c.payment?.provider_ref})`} />
                    <Chain label="Entitlement" val={`Bruto ${rp(c.entitlement?.gross_amount)} − Fee ${rp(c.entitlement?.platform_fee)} = Net ${rp(c.entitlement?.net_amount)}`} />
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* KYC documents modal */}
        <Dialog open={!!docModal} onOpenChange={(o) => !o && setDocModal(null)}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Dokumen KYC — {docModal?.name}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {docModal && Object.entries(docModal.documents).map(([k, url]) => (
                <a key={k} href={url} target="_blank" rel="noreferrer" data-testid={`doc-view-${k}`}
                  className="block rounded-lg border border-rl-border overflow-hidden hover:border-rl-primary transition-colors">
                  <img src={url} alt={k} className="w-full h-36 object-cover bg-rl-bg2" />
                  <div className="p-2 text-xs font-semibold text-rl-ink uppercase">{k.replace("_", " ")}</div>
                </a>))}
            </div>
          </DialogContent>
        </Dialog>
        {/* Reason modal (freeze / reject) */}
        <Dialog open={!!reasonModal} onOpenChange={(o) => !o && setReasonModal(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>{reasonModal?.title}</DialogTitle></DialogHeader>
            <p className="text-xs text-rl-muted">{reasonModal?.hint}</p>
            <textarea data-testid="reason-input" value={reasonText} onChange={(e) => setReasonText(e.target.value)}
              className="w-full rounded-lg border border-rl-border px-3 py-2 text-sm min-h-[80px] outline-none focus:border-rl-primary"
              placeholder="Tulis alasan..." />
            <DialogFooter>
              <button data-testid="reason-cancel" onClick={() => setReasonModal(null)} className="rounded-full border border-rl-border px-5 py-2 text-sm font-semibold">Batal</button>
              <button data-testid="reason-confirm" disabled={!reasonText.trim()} onClick={() => { reasonModal.onConfirm(reasonText.trim()); setReasonModal(null); }}
                className="rounded-full bg-rl-primary text-white font-bold px-6 py-2 text-sm disabled:opacity-50">Konfirmasi</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

const Table = ({ head, children }) => (
  <div className="rounded-xl border border-rl-border bg-white overflow-x-auto">
    <table className="w-full text-sm min-w-[560px]">
      <thead className="bg-rl-bg2 text-rl-muted text-xs uppercase"><tr>{head.map((h) => <th key={h} className="text-left p-3">{h}</th>)}</tr></thead>
      <tbody>{React.Children.count(children) ? children : <tr><td colSpan={head.length} className="p-8 text-center text-rl-muted">Tidak ada data.</td></tr>}</tbody>
    </table>
  </div>
);
const Badge = ({ ok, children }) => <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${ok ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{children}</span>;
const Chain = ({ label, val }) => <div className="flex gap-2"><span className="text-rl-muted w-24 shrink-0">{label}</span><span className="font-semibold text-rl-ink">{val}</span></div>;
